import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon, WbSunny as LightIcon, DarkMode as DarkIcon } from '@mui/icons-material';
import useToken from '../../hooks/useToken';
import useDebugTools from '../../hooks/useDebugTools';

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join('');
}

const NavBar = () => {
  const [fullName, setFullName] = useState(localStorage.getItem('fullName'));
  const [role, setRole] = useState(localStorage.getItem('role'));
  const [address, setAddress] = useState(localStorage.getItem('address'));
  const [accountId, setAccountId] = useState(localStorage.getItem('accountId'));
  const [idCopied, setIdCopied] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);
  const navigate = useNavigate();
  const { token, refreshAccessToken, removeToken, getValidToken } = useToken();
  const debugTools = useDebugTools();

  useEffect(() => {
    setFullName(localStorage.getItem('fullName'));
    setRole(localStorage.getItem('role'));
    setAddress(localStorage.getItem('address'));
    setAccountId(localStorage.getItem('accountId'));
  }, []);

  const handleLogout = async () => {
    let authToken = await getValidToken();
    if (!authToken) {
      removeToken();
      navigate('/', { replace: true });
      return;
    }

    try {
      const response = await fetch('/api/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      removeToken();
      localStorage.clear();
      navigate('/', { replace: true });
      window.location.reload();

      if (!response.ok) {
        console.error('Logout failed:', await response.json());
      }
    } catch (error) {
      console.error('An error occurred during logout:', error);
    }
  };

  const handleSave = async () => {
    let authToken = await getValidToken();
    if (!authToken) {
      removeToken();
      navigate('/', { replace: true });
      return;
    }

    try {
      const response = await fetch('/api/player/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ full_name: fullName, address }),
      });

      if (response.ok) {
        localStorage.setItem('fullName', fullName);
        localStorage.setItem('address', address);
        setShowModal(false);
      } else {
        console.error('Failed to update profile:', await response.json());
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  // Testing shortcut: backend accepts it only with DEBUG_TOOLS=true
  const handleResetProgress = async () => {
    if (!window.confirm('⚠️ DEBUG: Reset your progress to the beginning?')) return;

    let authToken = await getValidToken();
    if (!authToken) {
      removeToken();
      navigate('/', { replace: true });
      return;
    }

    try {
      const response = await fetch('/api/accounts/reset-progress', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (response.ok) {
        alert('Progress reset successfully! Redirecting to dashboard...');
        navigate('/dashboard', { replace: true });
        window.location.reload();
      } else {
        console.error('Failed to reset progress:', await response.json());
        alert('Failed to reset progress');
      }
    } catch (error) {
      console.error('Error resetting progress:', error);
      alert('Error resetting progress');
    }
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #12005e 0%, #3d1580 55%, #5e35b1 100%)',
          boxShadow: '0 2px 16px rgba(94, 53, 177, 0.45)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Toolbar variant="dense" sx={{ justifyContent: 'space-between', px: { xs: 1.5, md: 2 }, minHeight: '40px !important' }}>
          {/* Brand */}
          <Typography
            component={Link}
            to="/"
            variant="body1"
            sx={{
              fontWeight: 700,
              color: '#fff',
              textDecoration: 'none',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.95rem',
              letterSpacing: '0.02em',
              '&:hover': { color: '#d1c4e9' },
            }}
          >
            DocuPen Platform
          </Typography>

          {/* Right side */}
          {fullName ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Dashboard link */}
              <Button
                component={Link}
                to="/dashboard"
                size="small"
                sx={{
                  textTransform: 'none',
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  py: 0.25,
                  '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Dashboard
              </Button>

              {/* Participant ID chip */}
              {accountId && (
                <Tooltip title={idCopied ? 'Copied!' : `Participant ID: ${accountId} — click to copy`}>
                  <Chip
                    size="small"
                    label={`ID: ${accountId.slice(0, 8)}`}
                    onClick={() => {
                      navigator.clipboard.writeText(accountId);
                      setIdCopied(true);
                      setTimeout(() => setIdCopied(false), 2000);
                    }}
                    variant="outlined"
                    sx={{
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'rgba(255,255,255,0.7)',
                      fontFamily: 'monospace',
                      fontSize: '0.7rem',
                      height: 22,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.6)', color: '#fff' },
                    }}
                  />
                </Tooltip>
              )}

              {/* Avatar chip — opens profile modal */}
              <Tooltip title="Edit profile">
                <Chip
                  size="small"
                  avatar={
                    <Avatar
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: '#fff',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        width: 20,
                        height: 20,
                      }}
                    >
                      {getInitials(fullName)}
                    </Avatar>
                  }
                  label={fullName}
                  onClick={() => setShowModal(true)}
                  variant="outlined"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.4)',
                    color: '#fff',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: 24,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.7)' },
                  }}
                />
              </Tooltip>

              {/* Theme toggle */}
              <Tooltip title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
                <IconButton
                  size="small"
                  onClick={() => setDarkMode(d => !d)}
                  sx={{ color: 'rgba(255,255,255,0.85)', p: 0.5, '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                  {darkMode
                    ? <LightIcon sx={{ fontSize: 18 }} />
                    : <DarkIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>

              {/* Testing shortcut: only rendered with DEBUG_TOOLS=true */}
              {debugTools && (
              <Tooltip title="Debug: Reset Progress">
                <IconButton
                  size="small"
                  onClick={handleResetProgress}
                  sx={{ color: '#ffb74d', p: 0.25 }}
                >
                  <RefreshIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              )}

              {/* Logout */}
              <Button
                variant="outlined"
                size="small"
                onClick={handleLogout}
                sx={{
                  textTransform: 'none',
                  borderColor: 'rgba(255,255,255,0.45)',
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  py: 0.25,
                  borderRadius: '6px',
                  '&:hover': { backgroundColor: '#d32f2f', color: '#fff', borderColor: '#d32f2f' },
                }}
              >
                Logout
              </Button>
            </Box>
          ) : (
            <Button
              component={Link}
              to="/"
              size="small"
              sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.85)', fontWeight: 500, fontSize: '0.8rem' }}
            >
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Profile Dialog */}
      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Edit Profile</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {accountId && (
            <TextField
              label="Participant ID"
              value={accountId}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                sx: { fontFamily: 'monospace', fontSize: '0.8rem' },
                endAdornment: (
                  <Tooltip title={idCopied ? 'Copied!' : 'Copy ID'}>
                    <IconButton size="small" onClick={() => { navigator.clipboard.writeText(accountId); setIdCopied(true); setTimeout(() => setIdCopied(false), 2000); }}>
                      <span style={{ fontSize: 14 }}>{idCopied ? '✓' : '⎘'}</span>
                    </IconButton>
                  </Tooltip>
                ),
              }}
              helperText="Your unique participant ID — use this to log in"
            />
          )}
          <TextField
            label="Full Name"
            name="fullName"
            value={fullName || ''}
            onChange={(e) => setFullName(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Affiliation"
            name="address"
            value={address || ''}
            onChange={(e) => setAddress(e.target.value)}
            fullWidth
            size="small"
            placeholder="Enter your institution or affiliation"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowModal(false)} sx={{ textTransform: 'none', color: '#666' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              textTransform: 'none',
              backgroundColor: '#5e35b1',
              '&:hover': { backgroundColor: '#4527a0' },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NavBar;
